import { useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { MdStar } from 'react-icons/md';
import { HighlightedJson } from './ProductPreview';

// ── Search Result Row ─────────────────────────────────────────────────

interface SearchRowProps {
  result: Record<string, unknown>;
  isAd?: boolean;
}

const str = (val: unknown): string => (val == null ? '' : String(val));

const SearchResultRow = ({ result: r, isAd }: SearchRowProps) => {
  const position = str(r.position);
  const name = str(r.name);
  const asin = str(r.asin);
  const url = str(r.url);
  const price = str(r.price);
  const stars = Number(r.stars ?? r.rating ?? 0);
  const reviewCount = str(r.total_reviews ?? r.review_count);
  const isPrime = Boolean(r.is_prime);
  const isBestSeller = Boolean(r.is_best_seller);
  const isChoice = Boolean(r.is_amazons_choice);

  return (
    <div className='search-result-row' data-testid='search-result-row'>
      <span className='result-pos'>{position}</span>
      <div className='result-body'>
        <div className='result-name'>
          {url ? (
            <a href={url} target='_blank' rel='noopener noreferrer'>
              {name}
            </a>
          ) : (
            name
          )}
        </div>
        <div className='result-asin-line'>
          <span className='result-asin'>{asin}</span>
          {stars > 0 && (
            <span className='result-rating'>
              <MdStar className='star-filled' /> {stars} ({reviewCount})
            </span>
          )}
        </div>
      </div>
      <span className='result-price'>{price}</span>
      <div className='result-badges'>
        {isAd && <span className='pill pill-ad'>Ad</span>}
        {isPrime && <span className='pill pill-prime'>Prime</span>}
        {isBestSeller && <span className='pill pill-best'>Best Seller</span>}
        {isChoice && <span className='pill pill-choice'>A&apos;s Choice</span>}
      </div>
    </div>
  );
};

// ── Search Results Panel ──────────────────────────────────────────────

interface SearchResultsProps {
  data: Record<string, unknown>;
}

export const SearchResults = ({ data }: SearchResultsProps) => {
  const organic = useMemo(() => (data.results as Record<string, unknown>[]) || [], [data.results]);
  const ads = useMemo(() => (data.ads as Record<string, unknown>[]) || [], [data.ads]);
  const choice = useMemo(
    () => (data.amazons_choice as Record<string, unknown>[]) || [],
    [data.amazons_choice]
  );

  const tabs = useMemo(() => {
    const t = [{ key: 'organic', label: `Organic (${organic.length})` }];
    if (ads.length) {
      t.push({ key: 'ads', label: `Ads (${ads.length})` });
    }
    if (choice.length) {
      t.push({ key: 'choice', label: `Amazon's Choice (${choice.length})` });
    }
    t.push({ key: 'json', label: 'Raw JSON' });
    return t;
  }, [organic.length, ads.length, choice.length]);

  const [activeTab, setActiveTab] = useState('organic');

  const activeRows = useMemo(() => {
    switch (activeTab) {
      case 'ads':
        return ads;
      case 'choice':
        return choice;
      default:
        return organic;
    }
  }, [activeTab, organic, ads, choice]);

  return (
    <div className='search-results-panel' data-testid='search-results'>
      <Nav bsPrefix='result-tabs' activeKey={activeTab} onSelect={key => key && setActiveTab(key)}>
        {tabs.map(t => (
          <Nav.Item key={t.key}>
            <Nav.Link bsPrefix='result-tab' eventKey={t.key}>
              {t.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {activeTab === 'json' ? (
        <HighlightedJson data={data} />
      ) : (
        <div className='search-result-list'>
          {activeRows.length === 0 ? (
            <p className='scraper-muted'>No results</p>
          ) : (
            activeRows.map((r, i) => (
              <SearchResultRow key={i} result={r} isAd={activeTab === 'ads'} />
            ))
          )}
        </div>
      )}
    </div>
  );
};
